import groupBy from 'lodash/groupBy'
import uniqBy from 'lodash/uniqBy'
import { useCallback, useMemo, useState } from 'react'
import { AccessCodeInOrganization, Nullish, UserInfoInOrganization } from 'types'

type GroupedTable = OwnerGroup[]

interface OwnerGroup {
  user: Nullish<UserInfoInOrganization>
  regionalManagers: RegionalManagerGroup[]
}

interface RegionalManagerGroup {
  user: Nullish<UserInfoInOrganization>
  facilityManagers: FacilityManagerGroup[]
}

interface FacilityManagerGroup {
  user: Nullish<UserInfoInOrganization>
  supervisors: SupervisorGroup[]
}

interface SupervisorGroup {
  user: Nullish<UserInfoInOrganization>
  codes: AccessCodeInOrganization[]
}

export type ManagerRole = 'owner' | 'regionalManager' | 'facilityManager' | 'supervisor'

export interface StructureChanges {
  [key: string]: {
    [key in ManagerRole]?: Nullish<UserInfoInOrganization>
  }
}

export function useOrgAccessCodes(
  codes: AccessCodeInOrganization[],
  unassignedUsers: UserInfoInOrganization[],
) {
  const getUserKey = useCallback((user: Nullish<UserInfoInOrganization>) => {
    return user ? String(user.id) : 'null'
  }, [])

  const [structureChanges, setStructureChanges] = useState<StructureChanges>({})

  const codesWithChanges: AccessCodeInOrganization[] = useMemo(() => {
    return codes.map((code) => {
      const changes = structureChanges[code.id] || {}
      return {
        ...code,
        owner: changes.owner !== undefined ? changes.owner : code.owner,
        regionalManager:
          changes.regionalManager !== undefined ? changes.regionalManager : code.regionalManager,
        facilityManager:
          changes.facilityManager !== undefined ? changes.facilityManager : code.facilityManager,
        supervisor: changes.supervisor !== undefined ? changes.supervisor : code.supervisor,
      }
    })
  }, [codes, structureChanges])

  const orgStructure: GroupedTable = useMemo(() => {
    const groupedByOwner = groupBy(codesWithChanges, (item) => item.owner?.id)

    return Object.values(groupedByOwner).map((ownerGroup) => {
      const owner = ownerGroup[0].owner
      const groupedByRegional = groupBy(ownerGroup, (item) => getUserKey(item.regionalManager))

      const regionalManagers: RegionalManagerGroup[] = Object.values(groupedByRegional).map(
        (regionalGroup) => {
          const regionalManager = regionalGroup[0].regionalManager
          const groupedByFacility = groupBy(regionalGroup, (item) =>
            getUserKey(item.facilityManager),
          )

          const facilityManagers: FacilityManagerGroup[] = Object.values(groupedByFacility).map(
            (facilityGroup) => {
              const facilityManager = facilityGroup[0].facilityManager
              const groupedBySupervisor = groupBy(facilityGroup, (item) =>
                getUserKey(item.supervisor),
              )

              const supervisors: SupervisorGroup[] = Object.values(groupedBySupervisor).map(
                (supervisorGroup) => ({
                  user: supervisorGroup[0].supervisor,
                  codes: supervisorGroup,
                }),
              )

              return {
                user: facilityManager,
                supervisors,
              }
            },
          )

          return {
            user: regionalManager,
            facilityManagers,
          }
        },
      )

      return {
        user: owner,
        regionalManagers,
      }
    })
  }, [codesWithChanges, getUserKey])

  const getUsersForAssign = useCallback(
    (code: AccessCodeInOrganization, role: ManagerRole): UserInfoInOrganization[] => {
      const users: (UserInfoInOrganization | null)[] = []

      // find all users in the role
      codes.forEach((item) => {
        users.push(item[role])
      })

      // find all users in the code
      users.push(code.owner, code.regionalManager, code.facilityManager, code.supervisor)

      // add unassigned users
      users.push(...unassignedUsers)

      return uniqBy(users, (user) => user?.id).filter(Boolean) as UserInfoInOrganization[]
    },
    [codes, unassignedUsers],
  )

  const handleChangeCode = useCallback(
    (
      code: AccessCodeInOrganization,
      role: ManagerRole,
      newUser: Nullish<UserInfoInOrganization>,
    ) => {
      setStructureChanges((prev) => ({
        ...prev,
        [code.id]: {
          ...(prev[code.id] || {}),
          owner: code.owner,
          regionalManager: code.regionalManager,
          facilityManager: code.facilityManager,
          supervisor: code.supervisor,
          [role]: newUser,
        },
      }))
    },
    [],
  )

  const getCodesToChange = useCallback(
    (code: AccessCodeInOrganization, role: ManagerRole) => {
      const roles: ManagerRole[] = ['owner', 'regionalManager', 'facilityManager', 'supervisor']
      const prevRoles = roles.slice(0, roles.indexOf(role) + 1)

      return codesWithChanges.filter((codeItem) => {
        return prevRoles.every((roleItem) => {
          return codeItem[roleItem]?.id === code[roleItem]?.id
        })
      })
    },
    [codesWithChanges],
  )

  const handleChangeStructure = useCallback(
    (
      code: AccessCodeInOrganization,
      role: ManagerRole,
      newUser: Nullish<UserInfoInOrganization>,
    ) => {
      const codesToChange = getCodesToChange(code, role)

      const changes = codesToChange.reduce((acc, code) => {
        return {
          ...acc,
          [code.id]: {
            ...(acc[code.id] || {}),
            owner: code.owner,
            regionalManager: code.regionalManager,
            facilityManager: code.facilityManager,
            supervisor: code.supervisor,
            [role]: newUser,
          },
        }
      }, structureChanges)

      setStructureChanges((prev) => ({
        ...prev,
        ...changes,
      }))
    },
    [getCodesToChange, structureChanges],
  )

  const checkIsStructureValid = useCallback(() => {
    const notValidCodes = codesWithChanges.filter((code) => {
      return code.owner === null || code.supervisor === null
    })

    return notValidCodes.length === 0
  }, [codesWithChanges])

  const onResetStructure = useCallback(() => {
    setStructureChanges({})
  }, [])

  return {
    orgStructure,
    structureChanges,
    onChangeStructure: handleChangeStructure,
    onChangeCode: handleChangeCode,
    getUsersForAssign,
    getCodesToChange,
    checkIsStructureValid,
    onResetStructure,
  }
}
